import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { Button } from "@/ds/shadcn/components/ui/button";
import { ModalCreateCollection } from "@/features/collections/components/ModalCreateCollection";
import { QueryState } from "@/ds/custom/QueryState";
import { Statistic, StatisticsBar } from "@/ds/custom/StatisticsBar";
import type { CollectionModel } from "@/features/collections/api/models/CollectionModel";
import { useCollections } from "@/features/collections/api/useCollections";
import { CollectionSortOrderLabels } from "@/features/collections/CollectionSortOrder";
import { GalleryItem, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { MarketValueDisplay } from "@/features/common/components/MarketValueDisplay";
import { Padlock } from "@/features/common/components/Padlock";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { SubscriptionGate } from "@/features/common/components/SubscriptionGate";
import { useSubscription } from "@/features/common/api/useSubscription";
import { useDashboardCurrentPosition } from "@/features/dashboard/api/useDashboardCurrentPosition";
import { useDashboardHistoricalPositions } from "@/features/dashboard/api/useDashboardHistoricalPositions";
import { DashboardSection, DashboardSectionItems, DashboardSectionRow } from "@/features/dashboard/components/DashboardSection";
import { useEvItems } from "@/features/ev/api/useEvItems";
import { EvSmallProductDisplay } from "@/features/ev/components/EvSmallProductDisplay";
import { EvListSortLabels } from "@/features/ev/EvListSort";
import { EvProductTypeLabels } from "@/features/ev/EvProductType";
import { usePerformanceDeltas } from "@/features/performanceDeltas/api/usePerformanceDeltas";
import { useValuedCollectionsPerformanceDeltas } from "@/features/performanceDeltas/api/useValuedCollectionsPerformanceDeltas";
import { PerformanceDeltaSortLabels } from "@/features/performanceDeltas/PerformanceDeltaSort";
import { PerformanceDeltaStepLabels } from "@/features/performanceDeltas/PerformanceDeltaStep";
import { useTitle } from "@/lib/common/useTitle";
import { collectionArts } from "@/lib/media/collectionArts";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { BoxIcon, ChartNoAxesCombinedIcon, FolderPlusIcon, ScaleIcon } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router";

const CHART_BACKDROP_HISTORY_DAYS = 84;

export const PageDashboard: FC = () => {
	useTitle("Dashboard");

	const breadcrumb = useMemo(() => [
		{ label: "Dashboard" }
	], []);

	return (
		<>
			<HeaderBar title="Dashboard" description="An overview of your collections and the market" breadcrumb={breadcrumb} />
			<PageDashboardPortfolioSummary />
			<PageDashboardCollections />
			<PageDashboardTopSections />
		</>
	);
};

const PageDashboardTopSections: FC = () => {
	const { state } = useUserPreferencesState();
	const subscription = useSubscription();
	const collections = useCollections(state.collectionSortOrder, null);

	const isPremium = !!subscription.data?.activeSubscription;
	const hasCollections = !!collections.data?.pages.some(x => x.results.length > 0);

	if (!isPremium && !hasCollections) {
		return null;
	}

	return (
		<div className="grid grid-cols-1 divide-y border-b @lg:grid-cols-3 @lg:divide-x @lg:divide-y-0">
			<PageDashboardTopCollectionMovers />
			<PageDashboardTopMarketTrends />
			<PageDashboardTopEvItems />
		</div>
	);
};

const PageDashboardPortfolioSummary: FC = () => {
	const currentPosition = useDashboardCurrentPosition();
	const historicalPositions = useDashboardHistoricalPositions(CHART_BACKDROP_HISTORY_DAYS);

	return (
		<QueryState query={currentPosition}>
			{
				data => (
					<>
						<MarketValueDisplay
							marketValue={data.marketValue}
							invested={data.invested}
							gain={data.gain}
							historicalPositions={historicalPositions.data ?? []} />

						<StatisticsBar>
							<Statistic title="Invested">
								<Padlock on={data.invested}>
									{x => <MoneyDisplay mode="Standard" money={x} />}
								</Padlock>
							</Statistic>
							<Statistic title="Profit">
								<Padlock on={data.profit}>
									{x => <MoneyDisplay mode="Delta" money={x} />}
								</Padlock>
							</Statistic>
							<Statistic title="Cash Position">
								<Padlock on={data.cashPosition}>
									{x => <MoneyDisplay mode="Delta" money={x} />}
								</Padlock>
							</Statistic>
						</StatisticsBar>
					</>
				)
			}
		</QueryState>
	);
};

const PageDashboardCollections: FC = () => {
	const { state } = useUserPreferencesState();
	const collections = useCollections(state.collectionSortOrder, state.collectionFilterQuery);
	const [createOpen, setCreateOpen] = useState(false);

	const description = CollectionSortOrderLabels[state.collectionSortOrder] + (state.collectionFilterQuery ? ` · "${state.collectionFilterQuery}"` : "");

	return (
		<div className="border-b">
			<DashboardSection title="Collections" description={description} href="/collections">
				<DashboardSectionItems
					forQuery={collections}
					getKey={x => x.collectionId}
					emptyIcon={BoxIcon}
					emptyText="You don't have any collections yet."
					emptyAction={
						<Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
							<FolderPlusIcon />
							Create Collection
						</Button>
					}
					layout="Strip"
					renderItem={x => <PageDashboardCollectionsItem {...x} />} />
			</DashboardSection>

			<ModalCreateCollection open={createOpen} onOpenChange={setCreateOpen} />
		</div>
	);
};

const PageDashboardCollectionsItem: FC<CollectionModel> = props => {
	const {
		collectionId,
		name,
		description,
		image,
		marketValue,
		invested,
		gain
	} = props;

	const navigate = useNavigate();
	const collectionArt = useMemo(() => collectionArts[image], [image]);

	return (
		<GalleryItem title={name} description={description} imageUrl={collectionArt} isProductImage={false} className="aspect-402/331" onClick={() => navigate(`/collections/${collectionId}`)}>
			<GalleryItemProperty label="Market Value">
				<MoneyDisplay mode="Standard" money={marketValue} />
			</GalleryItemProperty>
			<GalleryItemProperty label="Gain">
				<Padlock on={invested && gain ? [invested, gain] : null}>
					{
						([x, y]) => <MoneyDisplay mode="DeltaPercentage" money={y} originalValue={x.value} />
					}
				</Padlock>
			</GalleryItemProperty>
		</GalleryItem>
	);
};

const PageDashboardTopCollectionMovers: FC = () => {
	const { state } = useUserPreferencesState();

	const description = `${PerformanceDeltaSortLabels[state.performanceDeltaSort]} · ${PerformanceDeltaStepLabels[state.performanceDeltaStep]} · Min ${state.performanceDeltaMinimum}`;

	return (
		<DashboardSection title="Top Movers in your Collections" description={description}>
			<SubscriptionGate featureName="Collection Trends" compact>
				<PageDashboardTopCollectionMoversItems />
			</SubscriptionGate>
		</DashboardSection>
	);
};

const PageDashboardTopCollectionMoversItems: FC = () => {
	const { state } = useUserPreferencesState();
	const performanceDeltas = useValuedCollectionsPerformanceDeltas(state.performanceDeltaStep, state.performanceDeltaMinimum, state.performanceDeltaSort);

	return (
		<DashboardSectionItems
			forQuery={performanceDeltas}
			getKey={x => x.variantId}
			emptyIcon={ChartNoAxesCombinedIcon}
			emptyText="None of the cards in your collections match your Market Trends settings."
			renderItem={x => (
				<DashboardSectionRow
					href={`/browse/product/${x.productId}`}
					leading={<SmallProductDisplay productName={x.productName} fcId={x.fcId} imageUrl={x.imageUrl} />}
					primary={<MoneyDisplay mode="Standard" money={x.currentPrice} />}
					secondary={<MoneyDisplay mode="DeltaPercentage" money={x.delta} originalValue={x.previousPrice.value} />} />
			)} />
	);
};

const PageDashboardTopMarketTrends: FC = () => {
	const { state } = useUserPreferencesState();

	const description = `${PerformanceDeltaSortLabels[state.performanceDeltaSort]} · ${PerformanceDeltaStepLabels[state.performanceDeltaStep]} · Min ${state.performanceDeltaMinimum}`;

	return (
		<DashboardSection title="Top Market Trends" description={description} href="/market-trends">
			<SubscriptionGate featureName="Market Trends" compact>
				<PageDashboardTopMarketTrendsItems />
			</SubscriptionGate>
		</DashboardSection>
	);
};

const PageDashboardTopMarketTrendsItems: FC = () => {
	const { state } = useUserPreferencesState();
	const performanceDeltas = usePerformanceDeltas("", state.performanceDeltaStep, state.performanceDeltaMinimum, state.performanceDeltaSort);

	return (
		<DashboardSectionItems
			forQuery={performanceDeltas}
			getKey={x => x.variantId}
			emptyIcon={ChartNoAxesCombinedIcon}
			emptyText="No market trends match your settings."
			renderItem={x => (
				<DashboardSectionRow
					href="/market-trends"
					leading={<SmallProductDisplay productName={x.productName} fcId={x.fcId} imageUrl={x.imageUrl} />}
					primary={<MoneyDisplay mode="Standard" money={x.currentPrice} />}
					secondary={<MoneyDisplay mode="DeltaPercentage" money={x.delta} originalValue={x.previousPrice.value} />} />
			)} />
	);
};

const PageDashboardTopEvItems: FC = () => {
	const { state } = useUserPreferencesState();

	const description = `${EvListSortLabels[state.evListSort]} · ${EvProductTypeLabels[state.evProductType]}`;

	return (
		<DashboardSection title="Top EV" description={description} href="/ev-tracker">
			<SubscriptionGate featureName="EV Tracker" compact>
				<PageDashboardTopEvItemsItems />
			</SubscriptionGate>
		</DashboardSection>
	);
};

const PageDashboardTopEvItemsItems: FC = () => {
	const { state } = useUserPreferencesState();
	const evItems = useEvItems(state.evListSort, state.evProductType);

	return (
		<DashboardSectionItems
			forQuery={evItems}
			getKey={x => x.productId}
			emptyIcon={ScaleIcon}
			emptyText="No EV items match your settings."
			renderItem={x => (
				<DashboardSectionRow
					href="/ev-tracker"
					leading={<EvSmallProductDisplay productName={x.productName} fcId={x.fcId} productImage={x.productImage} warning={x.warning} />}
					primary={<MoneyDisplay mode="Standard" money={x.currentEstimatedValue} />}
					secondary={<MoneyDisplay mode="DeltaPercentage" money={x.marketPriceDelta} originalValue={x.marketPrice.value} />} />
			)} />
	);
};
